import { Injectable, InjectionToken, inject } from '@angular/core';
import { getRegisteredTools, ToolMeta, toFoundryToolDefinition, FoundryToolDefinition } from './tool.decorator';

/**
 * Multi-provider token: every tool-provider service (a class with
 * `@AITool`-decorated methods) is registered into the app via:
 *
 *   { provide: AI_TOOL_PROVIDERS, useExisting: MyToolsService, multi: true }
 *
 * The registry walks the array on first use, harvests each instance's
 * `@AITool` metadata, and indexes by tool name for dispatch.
 */
export const AI_TOOL_PROVIDERS = new InjectionToken<readonly object[]>('AI_TOOL_PROVIDERS');

/** A tool call returned by the agent's Responses-API output. */
export interface AiToolCall {
  /** Foundry/OpenAI Responses-API correlation id used to wire result→call. */
  call_id: string;
  name: string;
  /** Raw JSON string of named arguments. May be empty or malformed. */
  arguments: string;
}

/**
 * Result of executing one tool call. `output` is whatever JSON-serialisable
 * value the tool returned (or an `{success:false, error}` envelope on failure).
 */
export interface AiToolCallOutput {
  call_id: string;
  output: unknown;
}

/**
 * Minimal WebMCP tool descriptor (`document.modelContext.registerTool`). The
 * spec is an origin-trial draft; we type only the fields we populate.
 */
export interface WebMcpToolDescriptor {
  name: string;
  description: string;
  inputSchema: object;
  execute(args: Record<string, unknown>): Promise<{ content: { type: 'text'; text: string }[] }>;
}

interface ToolEntry {
  meta: ToolMeta;
  instance: object;
}

@Injectable({ providedIn: 'root' })
export class AiToolRegistryService {
  #index: Map<string, ToolEntry> | null = null;
  private readonly providers: readonly object[] = inject(AI_TOOL_PROVIDERS, { optional: true }) ?? [];

  /** All tools known to the registry, in stable registration order. */
  list(): ToolMeta[] {
    return Array.from(this.#getIndex().values()).map(e => e.meta);
  }

  /** Foundry-shape tool definitions for posting to a prompt agent's `definition.tools`. */
  toFoundryToolDefinitions(): FoundryToolDefinition[] {
    return this.list().map(toFoundryToolDefinition);
  }

  /**
   * WebMCP tool descriptors for a browser-agent surface
   * (`document.modelContext.registerTool`). Twin of `toFoundryToolDefinitions()`:
   * pure — it returns descriptors and never touches the `modelContext` global.
   * The caller feature-detects `modelContext` and owns registration. Each tool's
   * input schema is the same JSON Schema already produced for Foundry; the handler
   * routes straight through `execute()` and wraps the result as MCP text content.
   */
  toWebMcpDescriptors(): WebMcpToolDescriptor[] {
    return this.list().map(meta => ({
      name: meta.name,
      description: meta.description,
      inputSchema: toFoundryToolDefinition(meta).parameters,
      execute: async (args: Record<string, unknown>) => ({
        content: [{
          type: 'text' as const,
          text: JSON.stringify(await this.execute(meta.name, JSON.stringify(args ?? {}))),
        }],
      }),
    }));
  }

  /**
   * Definitions for a named subset of registered tools (request-level `tools`
   * for direct-model calls). Unknown names are skipped with a warning so a
   * misspelled tool degrades to "model answers without it" instead of a crash.
   */
  toFoundryToolDefinitionsFor(names: string[]): FoundryToolDefinition[] {
    const index = this.#getIndex();
    const defs: FoundryToolDefinition[] = [];
    for (const name of names) {
      const entry = index.get(name);
      if (!entry) {
        console.warn(`[AiToolRegistry] Unknown tool requested: ${name}`);
        continue;
      }
      defs.push(toFoundryToolDefinition(entry.meta));
    }
    return defs;
  }

  hasAny(): boolean {
    return this.#getIndex().size > 0;
  }

  /**
   * Execute a tool call from the model. Returns the raw return value (any
   * JSON-serialisable shape); never throws — failures come back as
   * `{success:false, error, toolName}` so the agent can recover.
   *
   * Argument mapping: the model returns a JSON object of named args; we map
   * back to the method's positional signature using the order declared on
   * `@AITool(..., parameters)`.
   */
  async execute(name: string, argsJson: string): Promise<unknown> {
    const entry = this.#getIndex().get(name);
    if (!entry) {
      return { success: false, error: 'Unknown tool', toolName: name };
    }
    let args: Record<string, unknown> = {};
    try {
      args = argsJson ? JSON.parse(argsJson) : {};
    } catch (e) {
      console.warn(`[AiToolRegistry] Failed to parse args for ${name}:`, e);
    }
    try {
      const ordered = entry.meta.parameters.map(p => {
        const v = (args as Record<string, unknown>)[p.name];
        return v !== undefined ? v : p.default;
      });
      const method = (entry.instance as Record<string, (...a: unknown[]) => Promise<unknown>>)[entry.meta.methodName];
      return await method.apply(entry.instance, ordered);
    } catch (e) {
      console.error(`[AiToolRegistry] ${name} threw:`, e);
      return {
        success: false,
        error: 'Tool execution failed',
        toolName: name,
        details: e instanceof Error ? e.message : String(e),
      };
    }
  }

  /**
   * Runs the client tool a card action names. The action value is the tool name, optionally followed
   * by `:` and the value of the tool's first required parameter, for example `view_contact:<contact id>`.
   * Resolves with the tool name and the tool's output; failures come back as {@link execute} returns them.
   */
  async executeAction(value: string): Promise<{ name: string; output: unknown }> {
    const separator = value.indexOf(':');
    const name = (separator < 0 ? value : value.slice(0, separator)).trim();
    const argument = separator < 0 ? '' : value.slice(separator + 1).trim();
    const parameter = this.#getIndex().get(name)?.meta.parameters.find(p => p.required);
    const args = parameter && argument ? { [parameter.name]: argument } : {};
    return { name, output: await this.execute(name, JSON.stringify(args)) };
  }

  /** Lazy index build — touches DI'd provider instances exactly once. */
  #getIndex(): Map<string, ToolEntry> {
    if (this.#index) return this.#index;
    const index = new Map<string, ToolEntry>();
    for (const instance of this.providers) {
      const ctor = (instance as object).constructor;
      for (const meta of getRegisteredTools(ctor)) {
        if (index.has(meta.name)) {
          console.warn(`[AiToolRegistry] duplicate tool name "${meta.name}" — keeping first registration`);
          continue;
        }
        index.set(meta.name, { meta, instance });
      }
    }
    this.#index = index;
    return index;
  }
}
